import _ from 'lodash'

const regexCache = new Map<string, RegExp>()

function getOrCreateRegex(pattern: string, flags: string): RegExp {
  const key = `${flags}:${pattern}`
  let re = regexCache.get(key)
  if (!re) {
    re = new RegExp(pattern, flags)
    regexCache.set(key, re)
  }
  return re
}

export function camelCase(value: string): string {
  return _.camelCase(value)
}

export function capitalize(value: string): string {
  return _.capitalize(value)
}

export function formatKeyValuePairs(
  arr: Array<Record<string, string>>,
  labelReplacements: Record<string, string> = {},
  separator: string = "\n",
  shouldGroupByKey: boolean = true,
  keyPath: string = "label",
  valuePath: string = "value",
): string {
  let formattedItems: string[] = []
  if (!_.isEmpty(labelReplacements)) {
    for (const [target, replacement] of _.toPairs(labelReplacements)) {
      arr = _.map(arr, (item) => {
        const newItem = { ...item }
        let currentLabel = item[keyPath]
        currentLabel = currentLabel.replace(new RegExp(target, 'ig'), replacement);
        newItem[keyPath] = currentLabel
        return newItem
      })
    }
  }
  if (shouldGroupByKey) {
    const groups = _.groupBy(arr, (item) => item[keyPath])
    formattedItems = _.map(groups, (items, key) => {
      const values = _.map(items, (item) => item[valuePath])
      return `${key}: ${values.join(', ')}`
    })
  } else {
    formattedItems = _.map(arr, (item) => `${item[keyPath]}: ${item[valuePath]}`)
  }

  return formattedItems.join(separator)
}

export function lower(value: string): string {
  return _.toLower(value)
}

export function upper(value: string): string {
  return _.toUpper(value)
}

const NON_IDENTIFIER_CHARS = /[^A-Za-z0-9_-]/g
const EXCESSIVE_HYPHENS = /-{2,}/g
const EDGE_SEPARATORS = /^[-_]+|[-_]+$/g

export function toIdentifier(value: string): string {
  return _.toString(value)
    .replace(NON_IDENTIFIER_CHARS, '')
    .replace(EXCESSIVE_HYPHENS, '-')
    .replace(EDGE_SEPARATORS, '')
}

const NON_BUSINESS_NAME_CHARS = /[^\p{L}\p{N}_\-\s',&⭐.()/]/gu
const EXCESSIVE_SPACES = /\s+/g

export function toBusinessName(value: string): string {
  return _.toString(value)
    .replace(NON_BUSINESS_NAME_CHARS, '')
    .replace(EXCESSIVE_SPACES, ' ')
    .trim()
}

export function pad(...params: any[]): string {
  return _.pad.apply(null, params)
}

export function padStart(...params: any[]): string {
  return _.padStart.apply(null, params)
}

export function padEnd(...params: any[]): string {
  return _.padEnd.apply(null, params)
}

export function split(...params: any[]): string[] {
  return _.split.apply(null, params)
}

export function replace(...params: any[]): string {
  return _.replace.apply(null, params)
}

export function replaceAll(value: string, pattern: RegExp | string, replacement: string): string {
  if (_.isRegExp(pattern)) {
    const rePattern = pattern.source
    const reFlags = pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`
    const re = getOrCreateRegex(rePattern, reFlags)
    return _.toString(value).replace(re, replacement)
  } else {
    return _.toString(value).replace(getOrCreateRegex(pattern, 'g'), replacement)
  }
}

export function startsWith(...params: any[]): boolean {
  return _.startsWith(...params)
}

export function endsWith(...params: any[]): boolean {
  return _.endsWith(...params)
}

export function parseInt(...params: any[]): number {
  return _.parseInt.apply(null, params)
}

export function _parseFloat(value: string): number {
  return parseFloat(value)
}

export function trim(value: string, pattern: RegExp | string): string {
  if (_.isRegExp(pattern)) {
    const rePattern = pattern.source
    const reFlags = pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`
    const re = getOrCreateRegex(`^(${rePattern})+|(${rePattern})+$`, reFlags)
    return value.replace(re, '')
  } else {
    return _.trim(value, pattern)
  }
}

export function trimStart(value: string, pattern: RegExp | string): string {
  if (_.isRegExp(pattern)) {
    const rePattern = pattern.source
    const reFlags = pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`
    const re = getOrCreateRegex(`^(${rePattern})+`, reFlags)
    return value.replace(re, '')
  } else {
    return _.trimStart(value, pattern)
  }
}

export function trimEnd(value: string, pattern: RegExp | string): string {
  if (_.isRegExp(pattern)) {
    const rePattern = pattern.source
    const reFlags = pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`
    const re = getOrCreateRegex(`(${rePattern})+$`, reFlags)
    return value.replace(re, '')
  } else {
    return _.trimEnd(value, pattern)
  }
}

export function strip(value: string, pattern: RegExp | string): string {
  if (_.isRegExp(pattern)) {
    const rePattern = pattern.source
    const reFlags = pattern.flags.includes('g') ? pattern.flags : `${pattern.flags}g`
    const re = getOrCreateRegex(rePattern, reFlags)
    return _.toString(value).replace(re, '')
  } else {
    return _.toString(value).replace(getOrCreateRegex(pattern, 'g'), '')
  }
}

export function truncate(...params: any[]): string {
  return _.truncate.apply(null, params)
}

export function uriEncode(value: string): string {
  return encodeURI(value)
}

export function uriEncodeComponent(value: string): string {
  return encodeURIComponent(value)
}

export function uriDecode(value: string): string {
  return decodeURI(value)
}

export function uriDecodeComponent(value: string): string {
  return decodeURIComponent(value)
}

export function snakeCase(value: string): string {
  return _.snakeCase(value)
}

/**
 * Generates padded string variations for ES searching
 * @example `generateSearchVariants('abc', 3)` returns `['abc', 'ABC', '0abc', '00abc', '000abc']`
 */
export function generateSearchVariants(value: string, maxPrefix = 5, prefixChar = '0') {
  const results: string[] = [value, upper(value)]

  // trim any existing prefix pattern
  const existingPattern = new RegExp(`^${prefixChar}+`)
  const normalized = trimStart(value ?? "", existingPattern)

  for (let i = 1; i <= maxPrefix; i++) {
    results.push(prefixChar.repeat(i) + normalized)
  }

  return results
}

export function objectToString(
  value: any,
  withKeys: boolean = false,
  separator: string = ', '
): string {
  if (_.isObject(value)) {
    const entries = _.toPairs(value)
    return _.join(_.map(entries,
      ([key, val]) => withKeys ? `${key}: ${val}` : String(val)
    ), separator)
  }
  return String(value)
}
